import { motion } from 'framer-motion';
import { CheckCircle, Tag } from 'lucide-react';
import React from 'react';
import { Category } from './CategoryCard'; // Import the Category interface we defined earlier

// Define default colors to use if not provided
const defaultColors = {
  cardBackground: '#000000F2',
  cardBorder: '#DDB88919',
  cardBorderSelected: '#DDB889',
  cardShadow: '#DDB88919',
  cardShadowSelected: '#DDB88933',
  titleText: '#DDB889',
  subtitleText: '#9CA3AF',
  infoText: '#DDB889',
  iconColor: '#DDB889',
  selectionBackground: '#DDB889',
  selectionIcon: '#000000',
  badgeBackground: '#000000F2',
  badgeBorder: '#DDB88919',
  buttonBackground: '#DDB889',
  buttonText: '#000000'
};

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15
    }
  }
};

interface CategoryListProps {
  categories: Category[];
  selectedCategories: Category[];
  onToggle: (category: Category) => void;
  colors?: Partial<typeof defaultColors>;
}

export const CategoryList: React.FC<CategoryListProps> = ({
  categories,
  selectedCategories,
  onToggle,
  colors = {}
}) => {
  // Merge provided colors with defaults
  const themeColors = { ...defaultColors, ...colors };

  // Check if a category is selected
  const isCategorySelected = (category: Category) => {
    return selectedCategories.some(c => c.id === category.id);
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="lp-w-full lp-flex lp-flex-col lp-gap-2"
    >
      {categories.map((category) => (
        <motion.div
          key={category.id}
          variants={itemVariants}
          className="lp-flex lp-items-center lp-gap-3 lp-p-3 lp-rounded-xl lp-border lp-transition-all lp-duration-300 lp-cursor-pointer"
          style={{
            backgroundColor: themeColors.cardBackground,
            borderColor: isCategorySelected(category) ? themeColors.cardBorderSelected : themeColors.cardBorder,
            boxShadow: isCategorySelected(category)
              ? `0 0 15px ${themeColors.cardShadowSelected}`
              : `0 0 15px ${themeColors.cardShadow}`
          }}
          onClick={() => onToggle(category)}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
        >
          {/* Selection Circle */}
          <div className="lp-flex-shrink-0">
            <motion.div
              className="lp-w-10 lp-h-10 lp-rounded-full lp-flex lp-items-center lp-justify-center"
              style={{
                backgroundColor: isCategorySelected(category)
                  ? themeColors.selectionBackground
                  : 'transparent',
                border: isCategorySelected(category)
                  ? 'none'
                  : `1px solid ${themeColors.cardBorder}`
              }}
              whileHover={{ rotate: isCategorySelected(category) ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              {isCategorySelected(category) ? (
                <CheckCircle
                  className="lp-w-5 lp-h-5"
                  style={{ color: themeColors.selectionIcon }}
                />
              ) : (
                <Tag
                  className="lp-w-5 lp-h-5"
                  style={{ color: themeColors.iconColor }}
                />
              )}
            </motion.div>
          </div>

          {/* Category Name & Description */}
          <div className="lp-flex-grow lp-min-w-0">
            <h4
              className="lp-text-base lp-font-medium lp-truncate"
              style={{ color: themeColors.titleText }}
            >
              {category.name}
            </h4>
            <p
              className="lp-text-xs lp-truncate"
              style={{ color: themeColors.subtitleText }}
            >
              {category.description}
            </p>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
};

export default CategoryList;
